/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, Pencil, RefreshCw, Loader2 } from "lucide-react";
import { api } from "../lib/apiClient";
import { useMatch } from "../lib/MatchContext";
import RequireMatch from "../components/RequireMatch";
import { GenerateButton, VersionList } from "./PostEventReportPage";
import { deptPath, errMsg } from "../lib/reportApi";

export default function ReportPreviewPage() {
  const { selectedMatchId: M } = useMatch();
  const [pages, setPages] = useState(null);
  const [cur, setCur] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [dash, setDash] = useState(null);

  const build = async () => {
    setBusy(true); setErr("");
    try {
      const [{ data }, { data: d }] = await Promise.all([api.post(`/matches/${M}/report/preview`), api.get(`/matches/${M}/report`)]);
      setPages(data.pages); setDash(d); setCur((c) => Math.min(c, data.pages.length - 1));
    } catch (e) { setErr(errMsg(e, "Preview failed")); } finally { setBusy(false); }
  };
  useEffect(() => { if (M) build(); }, [M]);
  useEffect(() => {
    const k = (e) => {
      if (e.key === "ArrowRight") setCur((c) => Math.min(c + 1, (pages?.length || 1) - 1));
      if (e.key === "ArrowLeft") setCur((c) => Math.max(c - 1, 0));
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [pages]);

  if (!M) return <RequireMatch />;
  const page = pages?.[cur];
  const label = (p) => (p.type === "content" ? p.department : p.type[0].toUpperCase() + p.type.slice(1));

  return (
    <div className="p-6 md:p-10 pb-24" data-testid="page-report-preview">
      <Link to="/post-event-report" className="text-xs uppercase tracking-widest text-zinc-500 flex items-center gap-1 mb-4"><ArrowLeft className="w-3 h-3" /> Post Event Report</Link>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6 border-b border-cmdBorder pb-6">
        <div>
          <div className="mc-label">Exactly as exported · 16:9</div>
          <h1 className="font-head font-black text-4xl md:text-5xl uppercase tracking-tight leading-none mt-2">Preview Report</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={build} disabled={busy} className="border border-cmdBorder px-3 py-2 text-xs font-bold uppercase tracking-widest flex items-center gap-1 disabled:opacity-50" data-testid="report-preview-refresh">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Regenerate preview
          </button>
          {dash?.me.can_manage && <GenerateButton matchId={M} onDone={build} />}
        </div>
      </div>
      {err && <div className="text-incidentRed text-sm mb-4" data-testid="report-preview-error">{err}</div>}
      {!pages && busy && <div className="mc-panel aspect-video flex items-center justify-center text-zinc-500 text-sm gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Building pages…</div>}
      {page && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_220px] gap-4">
          <div>
            <div className="mc-panel p-2 relative">
              <img src={page.image} alt={`Page ${page.number}`} className="w-full aspect-video object-contain bg-zinc-100" data-testid="report-preview-image" />
              {busy && <div className="absolute inset-0 bg-white/60 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>}
            </div>
            <div className="flex items-center gap-3 mt-3">
              <button onClick={() => setCur((c) => Math.max(c - 1, 0))} disabled={cur === 0} className="border border-cmdBorder p-2 disabled:opacity-30" data-testid="report-preview-prev"><ChevronLeft className="w-4 h-4" /></button>
              <div className="font-mono-live text-sm" data-testid="report-preview-counter">Page {page.number} / {pages.length}</div>
              <button onClick={() => setCur((c) => Math.min(c + 1, pages.length - 1))} disabled={cur === pages.length - 1} className="border border-cmdBorder p-2 disabled:opacity-30" data-testid="report-preview-next"><ChevronRight className="w-4 h-4" /></button>
              <span className="text-sm text-zinc-500 truncate">{label(page)}{page.layout ? ` · ${page.layout} per page` : ""}</span>
            </div>
            {page.item_ids.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3" data-testid="report-preview-edit-links">
                {page.item_ids.map((id, i) => (
                  <Link key={id} to={`${deptPath(page.department)}?item=${id}`} className="border border-cmdBorder px-2 py-1 text-[11px] flex items-center gap-1 hover:border-black" data-testid={`report-preview-edit-${id}`}>
                    <Pencil className="w-3 h-3" /> Edit “{page.headings[i]}”
                  </Link>
                ))}
              </div>
            )}
            {dash && <div className="mc-panel mt-6"><div className="p-3 border-b border-cmdBorder mc-label">Generated versions</div><VersionList versions={dash.versions} /></div>}
          </div>
          <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-y-auto lg:max-h-[75vh]" data-testid="report-preview-thumbs">
            {pages.map((p, i) => (
              <button key={p.number} onClick={() => setCur(i)} data-testid={`report-preview-thumb-${p.number}`}
                className={`shrink-0 w-40 lg:w-full text-left border-2 ${i === cur ? "border-black" : "border-transparent"}`}>
                <img src={p.image} alt="" className="w-full aspect-video object-cover" loading="lazy" />
                <div className="text-[10px] px-1 py-0.5 truncate"><span className="font-mono-live">{p.number}</span> · {label(p)}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
